/**
 * OpenTelemetry Logger configuration for PostHog
 * Sends logs to PostHog using OTLP (OpenTelemetry Protocol)
 */

import { LoggerProvider, BatchLogRecordProcessor } from '@opentelemetry/sdk-logs'
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http'
import { resourceFromAttributes } from '@opentelemetry/resources'
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions'
import { logs } from '@opentelemetry/api-logs'
import { getCurrentEnvironment } from '../getEnvironment'

let loggerProvider: LoggerProvider | null = null
let hasWarnedMissingConfig = false

/**
 * Generates the service name for OTLP logging, including environment and branch information
 */
export function getServiceName(): string {
  const baseName = 'portals'
  const environment =
    process.env.CI === '1' || process.env.CI === 'true' ? 'ci' : getCurrentEnvironment()

  // For preview deployments, include the branch name
  if (environment === 'preview') {
    const branchName = process.env.VERCEL_GIT_COMMIT_REF
    if (branchName) {
      // Cap the full service name to 64 alphanumeric characters
      const sanitizedBranch = branchName.replace(/[^a-zA-Z0-9_-]/g, '-').substring(0, 48)
      return `${baseName}-${environment}-${sanitizedBranch}`
    }
  }

  return `${baseName}-${environment}`
}

/**
 * Initializes the OpenTelemetry logger with PostHog OTLP exporter
 */
export function initializeLogger(): void {
  // Don't initialize if already initialized
  if (loggerProvider) {
    return
  }

  // Don't initialize if required PostHog config is not available
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY || !process.env.POSTHOG_LOGS_ENDPOINT) {
    // Only warn once to avoid CI noise
    if (!hasWarnedMissingConfig) {
      console.warn('[Logger] Failed to initialize logger: Missing required environment variables')
      hasWarnedMissingConfig = true
    }
    return
  }

  try {
    const posthogApiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY
    const posthogLogsEndpoint = process.env.POSTHOG_LOGS_ENDPOINT

    // Validate URL format
    try {
      new URL(posthogLogsEndpoint)
    } catch {
      console.error('[Logger] Invalid POSTHOG_LOGS_ENDPOINT URL:', posthogLogsEndpoint)
      return
    }

    // Create resource with service information
    const serviceName = getServiceName()
    const resource = resourceFromAttributes({
      [ATTR_SERVICE_NAME]: serviceName,
      [ATTR_SERVICE_VERSION]: process.env.APP_VERSION || '1.0.0',
    })

    // Create OTLP exporter configured for PostHog
    const exporter = new OTLPLogExporter({
      url: posthogLogsEndpoint,
      headers: {
        Authorization: `Bearer ${posthogApiKey}`,
      },
    })

    // Create logger provider with batch processor to export logs (more efficient than SimpleLogRecordProcessor)
    loggerProvider = new LoggerProvider({
      resource,
      processors: [new BatchLogRecordProcessor({ exporter })],
    })

    // Set the global logger provider
    logs.setGlobalLoggerProvider(loggerProvider)
  } catch (error) {
    console.error('[Logger] Failed to initialize logger:', error)
    // Don't throw - allow the application to continue without OTLP logging
  }
}

/**
 * Checks if the logger provider is initialized
 * This is useful for determining if we should use OTLP logging or fall back to console
 */
export function isLoggerInitialized(): boolean {
  return loggerProvider !== null
}

/**
 * Shuts down the logger provider (useful for cleanup)
 */
export async function shutdownLogger(): Promise<void> {
  if (loggerProvider) {
    try {
      await loggerProvider.shutdown()
      loggerProvider = null
    } catch (error) {
      console.error('[Logger] Failed to shutdown logger:', error)
      // Reset loggerProvider even if shutdown failed to prevent retry issues
      loggerProvider = null
    }
  }
}
